import { gqlFetchList } from 'app/lib/graphqlClient'
import { myRoleId, ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import type { AssigneeCandidate, LocationScopedUser, RoleScopedUser } from 'app/types/location-access'
import { userHasLocationAccess } from 'lib/userLocationAccess'

// The location-access fields plus each user's role, which decides whether a role has anyone at the location.
const ASSIGNEE_FIELDS = `id name email type roleId allowedLocationIds assignedLocationIds workspaces { id locationIds roleId }`

type RosterUser = LocationScopedUser & RoleScopedUser & { id: string; name?: string; email?: string }

/**
 * Task assignee candidates: the workspace's roles, then its users who can see the task's location.
 * The search index cannot express "scoped to this location, or not scoped at all", so the
 * workspace roster is read whole and filtered here. Without a location it comes back unfiltered.
 */
export async function assigneesFetcher(params: {
  query: string
  locationId?: string
  workspaceId?: string
}): Promise<{ hits: AssigneeCandidate[]; total: number; page: number }> {
  const { query, locationId, workspaceId } = params
  const search = query ? `name:*${query}*` : undefined
  const [roster, roles] = await Promise.all([
    gqlFetchList<RosterUser>(`users`, { search, pagination: { page: 0, limit: 100 } }, ASSIGNEE_FIELDS),
    gqlFetchList<{ id: string; name: string }>(`roles`, { pagination: { page: 0, limit: 100 } }, `id name`),
  ])
  const users = locationId
    ? roster.hits.filter((user) => userHasLocationAccess(user, locationId, workspaceId))
    : roster.hits
  const heldRoleIds = new Set(users.map((user) => myRoleId(user, workspaceId)))
  const roleHits: AssigneeCandidate[] = roles.hits
    .filter((role) => role.name.toLowerCase().includes(query.toLowerCase()))
    .map((role) => ({
      id: `${ROLE_ASSIGNEE_PREFIX}${role.id}`,
      name: role.name,
      kind: `role`,
      disabled: !heldRoleIds.has(role.id),
    }))
  const hits = [...roleHits, ...users.map((user): AssigneeCandidate => ({ ...user, kind: `user` }))]
  return { hits, total: hits.length, page: 0 }
}
